import { EditorViewState, PluginViewStateAdapter, PluginWithViewState } from 'owa-editor';
import { IEditor, GetContentMode, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import {
    setHasAttachmentTriggerWords,
    setAttachmentKeywordsDetectedBeforeUserInput,
} from '../actions/internalActions';
import { ForgottenAttachmentsPluginViewState } from '../store/schema/ForgottenAttachmentsPluginViewState';
import { getCountOfAttachmentTriggerKeywords } from '../utils/getCountOfAttachmentTriggerKeywords';

const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';

export class ForgottenAttachmentsPlugin
    implements PluginWithViewState<ForgottenAttachmentsPluginViewState> {
    private editor: IEditor;
    private wasDisposed: boolean;

    /**
     * This will store the new trigger words we found since the editor ready on each extract content
     */
    private newTriggerWordsFound: number;

    constructor(
        readonly adapter: PluginViewStateAdapter<ForgottenAttachmentsPluginViewState>,
        readonly editorAdapter: PluginViewStateAdapter<EditorViewState>
    ) {
        this.newTriggerWordsFound = 0;
        this.wasDisposed = false;
    }

    public getName() {
        return 'ForgottenAttachments';
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;
    }

    public dispose(): void {
        this.wasDisposed = true;
    }

    public onPluginEvent(event: PluginEvent): void {
        /**
         * There are 4 scenarios we need to consider:
         * 1- Switch to another tab then switch back
         *    Timeline of operations/events: (EditorReady -> ExtractContentWithDom -> Dispose -> EditorReady)
         * 2- Up convert (When user starts with quick reply and then changes to full compose)
         *    Timeline of operations/events: (EditorReady -> ExtractContentWithDom -> Dispose -> EditorReady)
         * 3- Upgrade (e.g. Forward, as in this case the editor is first opened, a request is made to server
         *             to load the whole content of email and then added to the bdoy)
         *    Timeline of operations/events: (EditorReady -> ExtractContentWithDom -> ContentChanged[source=UpgradeCompose])
         * 4- Load draft (When this happens we can't distinguish between the text user entered
         *                and the text that was part of the message already [quoted body] so
         *                we don't handle this scenario.)
         * Condition: (event.eventType === PluginEventType.EditorReady &&
         *              (pluginViewState.attachmentKeywordsDetectedBeforeUserInput === null ||
         *                  this.wasDisposed))
         * This is setting `attachmentKeywordsDetectedBeforeUserInput` on editor ready event
         * when it was never set before (e.g. new compose) or in case of Up convert and switching scenario (#1, #2) as then
         * the editor will be disposed (indicated by `this.wasDisposed`) and EditorReady event will be sent
         *
         * Condition: (event.eventType === PluginEventType.ContentChanged &&
         *      (event as ContentChangedEvent).source === UPGRADE_CHANGE_SOURCE)
         * This is setting `attachmentKeywordsDetectedBeforeUserInput` on editor ready event
         * in case of Upgrade scenario (#3)
         */
        if (
            (event.eventType === PluginEventType.EditorReady &&
                (this.adapter().attachmentKeywordsDetectedBeforeUserInput === null ||
                    this.wasDisposed)) ||
            (event.eventType === PluginEventType.ContentChanged &&
                event.source === UPGRADE_CHANGE_SOURCE)
        ) {
            // We substract the `this.newTriggerWordsFound` as in case of scenario
            // where user switches from quick reply to full compose, tab switching, upgrade if the user added
            // a trigger word then we should not count that as otherwise the baseline
            // would include that trigger word and we would think user didn't type anything.
            const pluginViewState = this.adapter();
            const attachmentKeywordsDetectedBeforeUserInput =
                getCountOfAttachmentTriggerKeywords(
                    this.editor.getContent(GetContentMode.PlainText)
                ) - this.newTriggerWordsFound;

            setAttachmentKeywordsDetectedBeforeUserInput(
                pluginViewState,
                attachmentKeywordsDetectedBeforeUserInput
            );
            this.wasDisposed = false;
        } else if (event.eventType === PluginEventType.ExtractContentWithDom) {
            const pluginViewState = this.adapter();
            const updateAttachmentTriggerCount = getCountOfAttachmentTriggerKeywords(
                this.editor.getContent(GetContentMode.PlainText)
            );

            this.newTriggerWordsFound =
                updateAttachmentTriggerCount -
                pluginViewState.attachmentKeywordsDetectedBeforeUserInput;
            const hasAttachmentTriggerKeywords = this.newTriggerWordsFound > 0;

            setHasAttachmentTriggerWords(pluginViewState, hasAttachmentTriggerKeywords);
        }
    }
}
